"use client";

import { animate, motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import { useEstimator } from "../../_context/EstimatorContext";
import { DURATION } from "../../_logic/motion";
import type { UsdRange } from "../../_logic/types";

interface AnimatedAmountProps {
  value: UsdRange;
  /** Formats a USD range for display. */
  format: (range: UsdRange) => string;
}

/** How long a real change counts for, in seconds. */
const TWEEN_S = 0.35;

/** A gentle ease for the count, so the digits read throughout rather than blur at the start. */
const TWEEN_EASE = [0.4, 0, 0.2, 1] as const;

/**
 * Past this ratio between old and new, the amount isn't "changing", it's a different number:
 * swap it with a fade instead of counting through two orders of magnitude.
 */
const SWAP_RATIO = 3;

const isSwap = (from: UsdRange, to: UsdRange): boolean =>
  [
    [from.low, to.low],
    [from.high, to.high],
  ].some(([a, b]) => {
    if (a === 0 || b === 0) return a !== b;
    const ratio = Math.max(a, b) / Math.min(a, b);
    return ratio > SWAP_RATIO;
  });

/**
 * A money range that tweens to its new value instead of jumping, so a fare landing or an
 * assumption edit reads as a change rather than a flicker. A view switch (per person to total)
 * or a jump of more than {@link SWAP_RATIO}× is a different number, not a change, so it fades in
 * rather than counting. Under reduced motion every change is instant. Tabular figures keep the
 * width steady.
 * @param props - The USD range and its formatter.
 * @returns The animated text.
 * @throws Never.
 */
export const AnimatedAmount = ({ value, format }: AnimatedAmountProps) => {
  // Hooks
  const reduceMotion = useReducedMotion();
  const { costView } = useEstimator();

  // States
  const [shown, setShown] = useState(value);
  /** Bumped on each fade-in, so the span remounts and fades. */
  const [swap, setSwap] = useState(0);
  const shownRef = useRef(value);
  const viewRef = useRef(costView);

  // Effects
  useEffect(() => {
    const from = shownRef.current;
    const viewChanged = viewRef.current !== costView;
    viewRef.current = costView;
    if (from.low === value.low && from.high === value.high) return;
    if (reduceMotion || viewChanged || isSwap(from, value)) {
      shownRef.current = value;
      setShown(value);
      if (!reduceMotion) setSwap((count) => count + 1);
      return;
    }
    const controls = animate(0, 1, {
      duration: TWEEN_S,
      ease: TWEEN_EASE,
      onUpdate: (progress) => {
        const next = {
          low: from.low + (value.low - from.low) * progress,
          high: from.high + (value.high - from.high) * progress,
        };
        shownRef.current = next;
        setShown(next);
      },
    });
    return () => controls.stop();
  }, [value.low, value.high, costView, reduceMotion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render
  // A range may wrap after its dash, so a long one ("¥176.5K–317.8K") takes two lines in a narrow column.
  return (
    <motion.span
      key={swap}
      initial={swap === 0 ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: DURATION.fast }}
      className="whitespace-normal tabular-nums"
    >
      {format(shown).replace("–", "–​")}
    </motion.span>
  );
};
